import CloseIcon from '@mui/icons-material/Close';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import { tr } from '../lib/i18n';
import { Button } from './button';
import { ConfirmDialog, Loading } from './common';
import { LinkRow } from './ShareLinks';
import { useToast } from './Toast';

/** "Share" on a property: same dialog as the company links (QR + link + actions), plus WhatsApp. */
export function ShareProperty({ id, refNo, title, share, available }: { id: string; refNo: string; title: string; share: { url: string } | null; available: boolean }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['property', id] });

  const create = useMutation({
    mutationFn: () => api<{ url: string }>(`/properties/${id}/share`, { method: 'POST' }),
    onSuccess: refresh,
    onError: toastError,
  });
  const revoke = useMutation({
    mutationFn: () => api(`/properties/${id}/share`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmOff(false);
      setOpen(false);
      create.reset();
      toast(tr('تم إيقاف الرابط', 'Link turned off'));
      refresh();
    },
    onError: toastError,
  });

  const url = share?.url ?? create.data?.url ?? null;
  const openDialog = () => {
    setOpen(true);
    // a link is made only for a property that can be shown (available / reserved)
    if (!share && available && !create.isPending) create.mutate();
  };

  return (
    <>
      <Button variant="outlined" startIcon={<ShareIcon />} onClick={openDialog}>
        {tr('مشاركة', 'Share')}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <ShareIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">{tr('مشاركة العقار', 'Share property')}</Typography>
            <Typography variant="body2" color="text.secondary">{tr('انسخ الرابط أو أرسله عبر واتساب.', 'Copy the link or send it on WhatsApp.')}</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} aria-label={tr('إغلاق', 'Close')}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2.5 }}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {!available && (
              <Alert severity="warning">
                {share
                  ? tr('العقار غير متاح حالياً — من يفتح الرابط يرى أنه لم يعد متاحاً.', 'This property is not available now — the link shows that it is no longer available.')
                  : tr('يمكن مشاركة العقار المتاح أو المحجوز فقط.', 'Only an available or reserved property can be shared.')}
              </Alert>
            )}
            {!url ? (
              !available ? null :
              <Loading size="30px" />
            ) : (
              <LinkRow
                title={title}
                description={refNo}
                url={url}
                fileName={`${refNo}-qr`}
                extra={
                  <Button size="small" variant="outlined" color="success" startIcon={<WhatsAppIcon />} href={`https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`} target="_blank" rel="noopener noreferrer">
                    {tr('واتساب', 'WhatsApp')}
                  </Button>
                }
              />
            )}
            {url && (
              <Box>
                <Button size="small" color="error" startIcon={<LinkOffIcon />} onClick={() => setConfirmOff(true)}>
                  {tr('إيقاف الرابط', 'Turn off link')}
                </Button>
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOff}
        title={tr('إيقاف الرابط؟', 'Turn off the link?')}
        text={tr('لن يفتح الرابط بعد الآن.', 'The link will stop opening.')}
        confirmLabel={tr('إيقاف', 'Turn off')}
        danger
        busy={revoke.isPending}
        onConfirm={() => revoke.mutate()}
        onClose={() => setConfirmOff(false)}
      />
    </>
  );
}
